import type { FetchError } from 'ofetch'

import { createQueryKeys } from '@lukemorales/query-key-factory'

import type { UseQueryOptionsWithoutQueryFn } from '~/types/util/tanstack-query'

import { responseDataTypeName, useFetchRecommendation } from './useFetchRecommendation'

export const queryKeys = createQueryKeys(responseDataTypeName, {
  all: ({ workspaceId }: { workspaceId: MaybeRef<string> }) => ({
    queryKey: [{ workspaceId }],
  }),
  byDocumentId: ({ documentId, workspaceId }: { documentId: MaybeRef<string>, workspaceId: MaybeRef<string> }) => ({
    queryKey: [{ documentId, workspaceId }],
  }),
})

const all = ({ workspaceId, useQueryOptions }: {
  workspaceId: MaybeRef<string>
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
}) => {
  const { getAll } = useFetchRecommendation()

  return useQuery({
    queryKey: queryKeys.all({ workspaceId }).queryKey,
    queryFn: () => getAll({ workspaceId: unref(workspaceId) }),
    retry: (_, error: FetchError) => !!error.status && error.status !== 409,
    retryOnMount: false, // does not allow a method, so we can only fall back to the boolean
    refetchOnWindowFocus: false,
    ...useQueryOptions,
  })
}

const byDocumentId = ({
  documentId,
  useQueryOptions,
}: {
  documentId: MaybeRef<string>
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
}) => {
  const { getByDocumentId } = useFetchRecommendation()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    queryKey: queryKeys.byDocumentId({
      documentId,
      workspaceId: activeWorkspaceId.value as MaybeRef<string>,
    }).queryKey,
    queryFn: () => getByDocumentId({
      documentId: unref(documentId),
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    ...useQueryOptions,
  })
}

export const useQueryRecommendation = {
  all,
  byDocumentId,
}
